/**
 * Checks if an element is within a parent, considering both direct DOM containment
 * and virtual relationships through aria-controls attributes.
 *
 * This function handles cases where an element might be inside something that is
 * referenced by another element with aria-controls, which is directly or indirectly
 * contained in the parent (with recursion support).
 *
 * @param parent - The parent element to check containment against
 * @param child - The child element to check if it's within the parent
 * @returns true if the child is within the parent (directly or via aria-controls)
 */
export function isElementWithin(
  parent: HTMLElement,
  child: HTMLElement
): boolean {
  // First check direct DOM containment
  if (parent.contains(child)) {
    return true;
  }
  // Check for virtual relationships via aria-controls
  return checkAriaControlsRelationship(parent, child, new Set());
}

/**
 * Recursively checks for aria-controls relationships between elements.
 *
 * @param parent - The parent element
 * @param target - The target element we're looking for
 * @param visited - Set of visited elements to prevent infinite recursion
 * @returns true if a relationship is found
 */
function checkAriaControlsRelationship(
  parent: HTMLElement,
  target: HTMLElement,
  visited: Set<HTMLElement>
): boolean {
  // Prevent infinite recursion
  if (visited.has(parent)) {
    return false;
  }
  visited.add(parent);

  // Get all elements within the parent that have aria-controls
  const elementsWithAriaControls = parent.querySelectorAll('[aria-controls]');

  for (const element of elementsWithAriaControls) {
    const ariaControlsValue = element.getAttribute('aria-controls');
    if (!ariaControlsValue) continue;

    // Split aria-controls value (it can contain multiple IDs separated by spaces)
    const controlledIds = ariaControlsValue.trim().split(/\s+/);

    for (const id of controlledIds) {
      const controlledElement = document.getElementById(id);
      if (!controlledElement) continue;

      // Check if the target is within the controlled element
      if (controlledElement.contains(target)) {
        return true;
      }

      // Recursively check if the controlled element has aria-controls relationships
      // that might lead to the target
      if (checkAriaControlsRelationship(controlledElement, target, visited)) {
        return true;
      }
    }
  }

  return false;
}
